import type { FairUseWsMetadata, MyUsage, MyUsageRequestState, MyUsageStatus } from "@/api/fair-use"

import { PLAN_UPGRADE_REQUIRED_CODE } from "./fair-use-plan-utils"

// Non-component helpers for the fair-use user surface. Kept out of the .tsx
// component files so those export components only (react-refresh rule).

/** Indicator color bands per spec section 7: green < 50, yellow 50–75, orange 75–90, red >= 90.
 * Hues (no orange in the brand): green = success, yellow = warning, orange = soft
 * destructive, red = solid destructive. */
export type UsageColorBand = "green" | "yellow" | "orange" | "red"

const MY_USAGE_STATUSES: readonly string[] = ["ok", "warn", "downgrade", "block"]

function isMyUsageStatus(value: unknown): value is MyUsageStatus {
  return typeof value === "string" && MY_USAGE_STATUSES.includes(value)
}

/** Band ordering, least to most alarming — the tiebreak between the two signals. */
const BAND_SEVERITY: Record<UsageColorBand, number> = {
  green: 0,
  yellow: 1,
  orange: 2,
  red: 3,
}

/**
 * The band a backend status implies on its own, per spec section 2: `warn`
 * (>= the configured `warn_percent`) is the orange row, `downgrade` (over cap)
 * and `block` are red.
 */
const STATUS_BANDS: Record<MyUsageStatus, UsageColorBand> = {
  ok: "green",
  warn: "orange",
  downgrade: "red",
  block: "red",
}

/**
 * The band the raw percent implies (spec section 2's 50/75/90 brackets).
 *
 * Only NaN — a percent that is genuinely unknown — falls back to green.
 * `+Infinity` is spend against a zero cap, i.e. the worst case there is, and
 * must not be lumped in with NaN: `extractFairUseMetadata` admits any
 * `typeof percent === "number"`, so a malformed frame reporting infinite usage
 * used to render as a healthy green bar. It sorts into `>= 90` on its own.
 */
function percentBand(percent: number): UsageColorBand {
  if (Number.isNaN(percent)) return "green"
  if (percent >= 90) return "red"
  if (percent >= 75) return "orange"
  if (percent >= 50) return "yellow"
  return "green"
}

/**
 * THE single band selection for both fair-use surfaces — the composer
 * indicator/popover and the admin overview table. Keep it that way: two band
 * tables meant the same user could be orange in the composer and yellow in the
 * admin row at the same instant.
 *
 * Both signals are consulted and the MORE ALARMING one wins:
 * - `status` is authoritative in the only direction that matters — it can
 *   raise the band but never lower it — because the backend computes it from
 *   the admin's configured `warn_percent`/`downgrade_percent`/`block_percent`,
 *   which the 50/75/90 constants here do NOT track. A `warn` at 45 % (warn
 *   lowered to 40) is orange, and `downgrade`/`block` stay red however stale
 *   the percent is.
 * - `percent` may still escalate beyond the status, because the brackets above
 *   are the spec's own indicator scale: a `warn` user at 95 % reads red, not
 *   orange, whatever threshold produced the `warn`.
 * With no status at all (a WS metadata merge before the first snapshot) the
 * percent brackets decide alone.
 */
export function getUsageColorBand(percent: number, status?: MyUsageStatus): UsageColorBand {
  const byPercent = percentBand(percent)
  if (!status) return byPercent
  const byStatus = STATUS_BANDS[status]
  return BAND_SEVERITY[byStatus] > BAND_SEVERITY[byPercent] ? byStatus : byPercent
}

/** Tailwind `text-*` class per band; SVG ring + glyph inherit via currentColor. */
export function usageBandTextClass(band: UsageColorBand): string {
  switch (band) {
    case "yellow":
      return "text-warning"
    case "orange":
      // Soft red: destructive ink at 80 % keeps >=3:1 non-text contrast
      // (3.6:1 on white, 5.9:1 on the dark ground) while staying lighter than red.
      return "text-destructive/80"
    case "red":
      return "text-destructive"
    case "green":
    default:
      return "text-success"
  }
}

/** Progress-bar fill class per band (same palette as the admin fair-use table). */
export function usageProgressIndicatorClass(band: UsageColorBand): string {
  switch (band) {
    case "yellow":
      return "bg-warning-ink"
    case "orange":
      return "bg-destructive-ink/80"
    case "red":
      return "bg-destructive-ink"
    case "green":
    default:
      return "bg-success-ink"
  }
}

/** Percent clamped to 0–100 for ring/bar fills (real percent can exceed 100). */
export function clampPercentForDisplay(percent: number): number {
  if (!Number.isFinite(percent) || percent < 0) return 0
  return Math.min(percent, 100)
}

/** Whole-number percent for copy/aria; never negative. */
export function roundPercent(percent: number): number {
  if (!Number.isFinite(percent) || percent < 0) return 0
  return Math.round(percent)
}

/** Default "Request more" CTA threshold in percent of cap (spec section 2). */
export const REQUEST_CTA_PERCENT = 90

/** Usage shape both the CTA and the staleness check accept (WS merges lack `request`). */
type UsageCtaInput = Pick<MyUsage, "percent" | "status" | "request_cta_percent"> & {
  request?: MyUsageRequestState
}

/** Local verdict: does this snapshot sit at or past the point where budget can be asked for? */
export function meetsRequestCtaThreshold(
  usage: Pick<MyUsage, "percent" | "status" | "request_cta_percent">
): boolean {
  const threshold = usage.request_cta_percent ?? REQUEST_CTA_PERCENT
  return usage.percent >= threshold || usage.status === "downgrade" || usage.status === "block"
}

/**
 * "Request more" CTA visibility (spec: from the configured CTA, default 90 %).
 * Prefers the backend's own `request.allowed` verdict when present; a pending
 * request replaces the CTA. The `request` field is optional here on purpose —
 * a runtime payload merged from WS metadata may lack it, in which case the
 * local thresholds decide.
 */
export function shouldShowRequestCta(usage: UsageCtaInput): boolean {
  const request = usage.request
  if (request) {
    if (request.pending) return false
    return request.allowed
  }
  return meetsRequestCtaThreshold(usage)
}

/**
 * True when a snapshot's cached `request` verdict can no longer be trusted:
 * the user has moved into CTA territory while the cached verdict still says
 * "not allowed".
 *
 * `request.allowed` is only ever computed by GET /api/me/usage — the live WS
 * updates merge `{percent, status}` alone. A user who starts a session below
 * the configured threshold and is blocked (or crosses the CTA) mid-session
 * therefore keeps an `allowed:false` verdict forever, and
 * {@link shouldShowRequestCta} hides the CTA exactly when it is needed. The
 * bridge uses this to refetch the authoritative snapshot.
 */
export function hasStaleRequestVerdict(usage: UsageCtaInput): boolean {
  const request = usage.request
  // No cached verdict → the local thresholds already drive the CTA, and a
  // pending/allowed verdict is not stale (both already resolve the CTA).
  if (!request || request.pending || request.allowed) return false
  return meetsRequestCtaThreshold(usage)
}

// Intl.DateTimeFormat construction is comparatively expensive, so formatters
// are memoized per locale — one Map per options shape.
const monthNameFormatters = new Map<string, Intl.DateTimeFormat>()
const resetDateFormatters = new Map<string, Intl.DateTimeFormat>()

function getMemoizedFormatter(
  cache: Map<string, Intl.DateTimeFormat>,
  locale: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  let formatter = cache.get(locale)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options)
    cache.set(locale, formatter)
  }
  return formatter
}

/** "2026-08" → localized month name ("augusti"). Falls back to the raw key. */
export function formatMonthName(yearMonth: string, locale: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(yearMonth)
  if (!match) return yearMonth
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
  try {
    return getMemoizedFormatter(monthNameFormatters, locale, {
      month: "long",
      timeZone: "UTC",
    }).format(date)
  } catch {
    return yearMonth
  }
}

/** ISO timestamp → localized "1 september"-style day+month. Falls back to the raw value. */
export function formatResetDate(resetAtIso: string, locale: string): string {
  const date = new Date(resetAtIso)
  if (Number.isNaN(date.getTime())) return resetAtIso
  try {
    return getMemoizedFormatter(resetDateFormatters, locale, {
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    }).format(date)
  } catch {
    return date.toISOString().slice(0, 10)
  }
}

/** SEK amount with two decimals; "-" for missing/non-finite values. Shared with the admin surface. */
export function formatSek(
  value: number | null | undefined,
  options?: { withUnit?: boolean }
): string {
  // Number.isFinite, not just Number.isNaN: an Infinity that slipped past an
  // upstream guard would otherwise render as the literal string "Infinity" —
  // seen in practice via the fair-use error-frame path (readErrorUsage below)
  // before it validated cost_sek/cap_sek the same way readAmount does.
  if (value == null || !Number.isFinite(value)) return "-"
  const formatted = value.toFixed(2)
  return options?.withUnit ? `${formatted} SEK` : formatted
}

/** Shorten an oid-like id ("aaaaaaaa-bbbb-…" → "aaaaaaaa...cdef") for display. */
export function truncateOid(id: string): string {
  return `${id.slice(0, 8)}...${id.slice(-4)}`
}

/** A money amount is only usable if it is a real, finite number of kronor. */
function readAmount(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

/**
 * Extract the `fair_use: { percent, status, cost_sek, cap_sek }` metadata the
 * gate appends to the final WS message of a completed answer (spec section 5).
 * On the wire the done frame carries it nested under `context` (`{ completed,
 * context: { ..., fair_use } }` — approach.py puts it in the final chunk's
 * context and websocket_decorators.py lifts that context into the done frame),
 * so both the top level and `context` are checked. Returns null when the
 * payload carries none — the common case for every non-fair-use message.
 *
 * `percent` + `status` are required; the two money fields are optional and are
 * only carried through when they are finite numbers. Anything else (a string,
 * NaN, an infinite amount, an older backend that omits them entirely) is
 * reported as "no amount" rather than as a figure — {@link
 * mergeFairUseWsMetadata} then makes sure no wrong amount is shown.
 */
export function extractFairUseMetadata(payload: unknown): FairUseWsMetadata | null {
  if (!payload || typeof payload !== "object") return null
  const record = payload as { fair_use?: unknown; context?: unknown }
  let fairUse = record.fair_use
  if (
    (!fairUse || typeof fairUse !== "object") &&
    record.context &&
    typeof record.context === "object"
  ) {
    fairUse = (record.context as { fair_use?: unknown }).fair_use
  }
  if (!fairUse || typeof fairUse !== "object") return null
  const { percent, status, cost_sek, cap_sek } = fairUse as {
    percent?: unknown
    status?: unknown
    cost_sek?: unknown
    cap_sek?: unknown
  }
  if (typeof percent !== "number" || !isMyUsageStatus(status)) return null
  const metadata: FairUseWsMetadata = { percent, status }
  const cost = readAmount(cost_sek)
  if (cost !== undefined) metadata.cost_sek = cost
  const cap = readAmount(cap_sek)
  if (cap !== undefined) metadata.cap_sek = cap
  return metadata
}

/**
 * Fold live WS metadata into the cached snapshot without ever letting the
 * percent and the spend figure contradict each other.
 *
 * The bug this exists for: the metadata used to carry `{percent, status}`
 * alone, so the merge left `cost_sek` at whatever the session's first GET
 * /api/me/usage had fetched. Against a 2 SEK cap the popover then read
 * "You have used 68 %" directly above "0.00 of 2.00 SEK" — two statements
 * about the same month, one of them false.
 *
 * With the amounts present (current backend) they simply replace the cached
 * ones. Without them (older backend) the cost is set to `null` — "unknown" —
 * as soon as the percent has moved, and the popover drops the spend line for
 * that render. Showing nothing is strictly better than showing a number that
 * is known to be wrong, and the very next frame that carries an amount (or the
 * next snapshot fetch) restores the line.
 *
 * Two things are deliberately NOT invalidated by a missing amount:
 * - an UNCHANGED percent: nothing has moved under the cached cost, so it is
 *   still the right figure (and leaving it alone keeps an idle repeat frame a
 *   pure no-op instead of a cache write);
 * - `cap_sek`: the cap is a configured value, not a measurement of this turn.
 *   It only changes when an admin grants budget, which arrives through the
 *   authoritative snapshot rather than through a turn's metadata.
 */
export function mergeFairUseWsMetadata(current: MyUsage, metadata: FairUseWsMetadata): MyUsage {
  const { cost_sek: cost, cap_sek: cap, percent, status } = metadata
  const merged: MyUsage = { ...current, percent, status }
  if (cost !== undefined) {
    merged.cost_sek = cost
  } else if (percent !== current.percent) {
    merged.cost_sek = null
  }
  if (cap !== undefined) merged.cap_sek = cap
  return merged
}

/** Usage snapshot carried by a structured fair-use error (QuotaSnapshot.to_dict subset). */
export type FairUseErrorUsage = Partial<
  Pick<
    MyUsage,
    "enabled" | "year_month" | "cost_sek" | "cap_sek" | "percent" | "status" | "reset_at"
  >
>

/**
 * The stable error codes the fair-use gate puts on a structured WS error frame.
 *
 * - `fair_use_blocked` — the hard block: the monthly budget is spent and NO
 *   turn can be served until it is raised or resets.
 * - `fair_use_deep_unavailable` — a Deep Explorer run was REFUSED BEFORE IT
 *   STARTED because the user sits at or above the downgrade threshold. One deep
 *   run can burn a large fraction of a monthly cap and the gate can only admit
 *   or refuse a turn, never stop it mid-flight, so it declines to start one this
 *   close to the cap. Ordinary chat still works — this is emphatically not
 *   "your budget is finished", and the two must never share copy.
 * - `plan_upgrade_required` — the feature is NOT IN THE USER'S PLAN (fair use
 *   round 2). Nothing about the month's budget is wrong; spending less or
 *   waiting for the reset changes nothing, and asking for more budget is the
 *   wrong CTA. It is the third code here rather than its own detector so that
 *   every refusal keeps arriving through one seam
 *   ({@link extractFairUseError} → routeFairUseError → the fair-use card).
 */
const FAIR_USE_ERROR_CODES = [
  "fair_use_blocked",
  "fair_use_deep_unavailable",
  PLAN_UPGRADE_REQUIRED_CODE,
] as const

export type FairUseErrorCode = (typeof FAIR_USE_ERROR_CODES)[number]

function asFairUseErrorCode(value: unknown): FairUseErrorCode | undefined {
  return typeof value === "string" && (FAIR_USE_ERROR_CODES as readonly string[]).includes(value)
    ? (value as FairUseErrorCode)
    : undefined
}

function readErrorCandidate(candidate: unknown): {
  code?: FairUseErrorCode
  usage?: unknown
} {
  if (!candidate || typeof candidate !== "object") return {}
  const record = candidate as Record<string, unknown>
  return {
    code: asFairUseErrorCode(record.error) ?? asFairUseErrorCode(record.code),
    usage: record.usage,
  }
}

/** A structured fair-use WS error: which refusal it was, plus what it knew about usage. */
export type FairUseErrorPayload = {
  code: FairUseErrorCode
  usage: FairUseErrorUsage
}

/**
 * Detect a structured fair-use WS error and extract its code + usage snapshot.
 * Tolerant of the payload arriving at the error frame's top level or nested
 * under `data` (`FairUseBlockedError.to_payload()` is `{ "error":
 * "fair_use_blocked", "usage": {...} }`, and the deep refusal rides the same
 * frame shape with its own code). Returns null for every other error; `usage`
 * is `{}` when the frame matched but carried no usable usage payload.
 */
export function extractFairUseError(errorMsg: unknown): FairUseErrorPayload | null {
  if (!errorMsg || typeof errorMsg !== "object") return null
  const record = errorMsg as Record<string, unknown>

  const candidates = [record, record.data]
  let usagePayload: unknown
  // A frame whose `message` IS the bare code (no envelope at all) still counts.
  let code = asFairUseErrorCode(record.message)
  for (const candidate of candidates) {
    const result = readErrorCandidate(candidate)
    if (result.code) {
      code = result.code
      if (result.usage !== undefined) usagePayload = result.usage
    }
  }
  if (!code) return null

  return { code, usage: readErrorUsage(usagePayload) }
}

/** Keep only the usage fields that arrived as the right type — never poison the cache. */
function readErrorUsage(usagePayload: unknown): FairUseErrorUsage {
  if (!usagePayload || typeof usagePayload !== "object") return {}
  const usage = usagePayload as Record<string, unknown>
  const picked: FairUseErrorUsage = {}
  if (typeof usage.enabled === "boolean") picked.enabled = usage.enabled
  if (typeof usage.year_month === "string") picked.year_month = usage.year_month
  // readAmount, not a bare typeof check: cost_sek/cap_sek must be REAL,
  // FINITE numbers of kronor — same rule readAmount already enforces for the
  // done-frame metadata path above. Without it, a NaN/Infinity that slipped
  // through an upstream bug would reach formatSek and render as the literal
  // string "Infinity" in the popover, at exactly the moment the user is told
  // they're blocked.
  const cost = readAmount(usage.cost_sek)
  if (cost !== undefined) picked.cost_sek = cost
  const cap = readAmount(usage.cap_sek)
  if (cap !== undefined) picked.cap_sek = cap
  const percent = readAmount(usage.percent)
  if (percent !== undefined) picked.percent = percent
  if (isMyUsageStatus(usage.status)) picked.status = usage.status
  if (typeof usage.reset_at === "string") picked.reset_at = usage.reset_at
  return picked
}
